import BigNumber from "bignumber.js";
import type {
  RetirementData,
  RetirementResult,
} from "../store/retirementStore";
import type { SettingsCategory } from "../types/settings";
import {
  formatCurrency,
  displayPercentage,
  displayYears,
  displayWholeNumber,
} from "./formatting";

export interface Recommendation {
  title: string;
  description: string;
  keyActions?: string[]; // Optional array of action items
  type: "success" | "warning" | "error" | "info";
  priority: number; // 1 = highest priority, higher numbers = lower priority
  /** Settings category to open when user clicks "Adjust →" */
  settingsCategory?: SettingsCategory;
}

/**
 * Generate personalized retirement recommendations based on user data and calculations
 * Returns up to 3 recommendations, sorted by priority
 */
export function generateRecommendations(
  data: RetirementData,
  result: RetirementResult
): Recommendation[] {
  const recommendations: Recommendation[] = [];

  // Rule 1: Critical - Income below expenses (highest priority)
  if (result.incomeCoverage !== undefined && result.incomeCoverage < 0.75) {
    recommendations.push({
      title: "Income May Not Cover Expenses",
      description: `Your projected retirement income (${formatCurrency(result.monthlyIncome, "CAD")}) covers only ${displayPercentage(result.incomeCoverage)} of your monthly expenses (${formatCurrency(data.monthlyExpenses, "CAD")}). This gap could deplete your savings faster than expected.`,
      keyActions: [
        "Increase your monthly contributions",
        "Reduce planned expenses in retirement",
        "Consider delaying retirement by 2-3 years",
      ],
      type: "error",
      priority: 1,
      settingsCategory: "income",
    });
  }

  if (result.readinessScore < 50) {
    const projectedAmount = new BigNumber(500)
      .times(12)
      .times(result.yearsToRetirement)
      .times(new BigNumber(1).plus(data.annualReturn / 2))
      .dividedBy(1000)
      .integerValue()
      .times(1000)
      .toNumber();

    recommendations.push({
      title: "Increase Monthly Savings",
      description: `Your readiness score of ${displayWholeNumber(result.readinessScore)}% indicates significant risk. Increasing your monthly contributions by even $500 can have a substantial impact due to compound interest over ${displayYears(result.yearsToRetirement)} years. At a ${displayPercentage(data.annualReturn)} return, an additional $500/month could add approximately ${formatCurrency(projectedAmount, "CAD")} to your retirement fund.`,
      type: "error",
      priority: 2,
      settingsCategory: "investableAssets",
    });

    recommendations.push({
      title: "Consider Delaying Retirement",
      description: `Your current plan targets retirement at age ${displayWholeNumber(data.retirementAge)}. Working an additional 2-3 years could significantly improve your retirement security.`,
      keyActions: [
        "Allows more time for compound growth",
        "Increases your final savings amount",
        "Reduces the number of years your savings must last",
      ],
      type: "warning",
      priority: 2,
      settingsCategory: "assumptions",
    });
  }

  // Rule 3: Early retirement warning
  if (data.retirementAge < 60 && result.readinessScore < 70) {
    recommendations.push({
      title: "Early Retirement is Ambitious",
      description: `Retiring at age ${displayWholeNumber(data.retirementAge)} means your savings must last approximately ${displayYears(90 - data.retirementAge)} years (assuming life expectancy of 90). Your current readiness score of ${displayWholeNumber(result.readinessScore)}% suggests your savings rate may not be sufficient for such an extended retirement.`,
      keyActions: [
        "Increase contributions to 20-25% of income",
        "Consider adjusting retirement age to 62-65",
      ],
      type: "warning",
      priority: 3,
      settingsCategory: "assumptions",
    });
  }

  // Rule 4: Low savings rate
  if (result.savingsRate !== undefined && result.savingsRate < 10) {
    recommendations.push({
      title: "Increase Your Savings Rate",
      description: `Your current savings rate of ${displayPercentage(result.savingsRate / 100)} is below the recommended 15-20% threshold. Financial planners suggest saving at least 15% of gross income for retirement to maintain your lifestyle.`,
      keyActions: [
        "Review your budget to identify areas for expense reduction",
        "Increase retirement account contributions",
        "Consider automatic savings transfers",
      ],
      type: "warning",
      priority: 3,
      settingsCategory: "expenses",
    });
  }

  // Rule 5: Optimistic return rate (annualReturn is stored as decimal, e.g. 0.065 = 6.5%)
  if (data.annualReturn > 0.08) {
    recommendations.push({
      title: "Consider More Conservative Returns",
      description: `Your expected return of ${displayPercentage(data.annualReturn)} is optimistic. Historical data shows: Canadian balanced portfolios (60% stocks/40% bonds) averaged 6-7% annually over the past 30 years. Using overly optimistic assumptions may result in disappointment.`,
      keyActions: [
        "Consider using 6.5-7% for more realistic planning",
        "Review your asset allocation",
        "Consult with a financial advisor",
      ],
      type: "info",
      priority: 4,
      settingsCategory: "assumptions",
    });
  }

  // Rule 6: Medium score - needs attention
  if (result.readinessScore >= 50 && result.readinessScore < 80) {
    recommendations.push({
      title: "You're Making Progress",
      description: `Your readiness score of ${displayWholeNumber(result.readinessScore)}% indicates you're on a reasonable track. However, improving to 80%+ would provide greater security. Small improvements now compound significantly over ${displayYears(result.yearsToRetirement)} years.`,
      keyActions: [
        "Increase monthly contributions by 5-10%",
        "Maintain consistent savings habits",
        "Avoid withdrawing from retirement accounts early",
      ],
      type: "warning",
      priority: 5,
      settingsCategory: "investableAssets",
    });
  }

  // Rule 7: Success - on track
  if (result.readinessScore >= 80) {
    const coverageText =
      result.incomeCoverage !== undefined
        ? ` at ${displayPercentage(result.incomeCoverage)} of your expenses`
        : "";
    recommendations.push({
      title: "You're On Track!",
      description: `Excellent work! Your readiness score of ${displayWholeNumber(result.readinessScore)}% indicates strong retirement preparation. Your projected monthly income of ${formatCurrency(result.monthlyIncome, "CAD")}${coverageText} should provide financial security.`,
      keyActions: [
        "Maintain consistent contributions",
        "Review your plan annually",
        "Adjust for major life changes",
      ],
      type: "success",
      priority: 6,
    });
  }

  // Sort by priority (ascending) and return top 3
  return recommendations.sort((a, b) => a.priority - b.priority).slice(0, 3);
}
